import { useCallback, useRef } from "react";
import { TellescopeModelFromName, useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import {
  CONTACT_TIMELINE_TAKEOVER_VIEW_ID,
  getDefaultView,
  isFetchableViewId,
} from "../components/ContactDetailsHeader/constants";

/**
 * The view a mutation may write to: its id, plus the record itself when this resolution is what
 * created it.
 *
 * `created` exists because a just-materialized view is not in the query cache yet, so a caller that
 * needs the view's current blocks as a base has nothing to read. Treating that cache miss as an empty
 * view is what makes a `replaceObjectFields: true` write destroy the record's contents, so the
 * authoritative base has to travel with the id. It is `undefined` for an already-saved view, whose
 * record the cache is the right source for.
 */
export interface WritableView {
  id: string;
  created: TellescopeModelFromName<"custom_dashboards"> | undefined;
}

export interface UseWritableViewIdArgs {
  viewId: string;
  /**
   * Called with the id of the newly created view when the placeholder had to be materialized. Wire
   * this to whatever owns view selection so it can move off the sentinel — otherwise the body keeps
   * rendering the local placeholder and the write that just landed is invisible.
   */
  onViewMaterialized?: (viewId: string) => void;
}

/**
 * Resolves the `custom_dashboards` id a mutation may write to.
 *
 * The placeholder view (`DEFAULT_CONTACT_DETAILS_VIEW_ID`) is synthesized client-side by
 * {@link getDefaultView} and has no server record, so writing to it would target something that does
 * not exist. This hook turns it into a real record on first write and reports the new id via
 * `onViewMaterialized`. A view id that already refers to a saved record passes straight through.
 *
 * Resolves to a {@link WritableView} rather than a bare id, so a caller building a
 * `replaceObjectFields: true` payload can base it on the freshly created record during the window
 * before that record reaches the query cache.
 *
 * @example
 * const resolveWritableView = useWritableViewId({ viewId, onViewMaterialized });
 * const { id, created } = await resolveWritableView();
 */
export const useWritableViewId = ({ viewId, onViewMaterialized }: UseWritableViewIdArgs) => {
  const { currentUser } = useAuth();

  const createViewMutation = useTellescopeMutation({
    model: "custom_dashboards",
    method: "createOne",
  });

  /**
   * The create in flight, so concurrent or repeated resolutions share one record rather than each
   * creating their own view. Cleared on failure so a later attempt can retry.
   *
   * Keyed on the `viewId` it was created for. Without the key the id outlives the record: create a
   * view, delete it, and land back on the placeholder — the cached promise would hand back the
   * deleted id and the write would target a record that no longer exists.
   */
  const materializingRef = useRef<{ viewId: string; promise: Promise<WritableView> } | undefined>(undefined);

  return useCallback(() => {
    // Hard backstop: this sentinel must never be promoted to a real record, even if a future caller reaches this hook for it.
    if (viewId === CONTACT_TIMELINE_TAKEOVER_VIEW_ID) {
      return Promise.reject(new Error("Cannot materialize a real view for the contact timeline takeover sentinel."));
    }
    if (isFetchableViewId(viewId)) return Promise.resolve({ id: viewId, created: undefined });
    if (materializingRef.current?.viewId === viewId) return materializingRef.current.promise;

    const defaultView = getDefaultView();
    const materializing = createViewMutation
      .mutateAsync({
        title: defaultView.title,
        type: defaultView.type,
        blocks: defaultView.blocks,
        userIds: [currentUser.id],
      })
      .then((created) => {
        onViewMaterialized?.(created.id);
        return { id: created.id, created };
      });

    materializingRef.current = { viewId, promise: materializing };

    return materializing.catch((error: unknown) => {
      if (materializingRef.current?.viewId === viewId) materializingRef.current = undefined;
      throw error;
    });
  }, [createViewMutation, currentUser.id, onViewMaterialized, viewId]);
};
